import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';

import classes from './TechBackground.module.scss';

// three.js is loaded client-side only, after the page content has painted.
const BackgroundCanvas = dynamic(() => import('./BackgroundCanvas'), { ssr: false });

const TechBackground = () => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [canvasReady, setCanvasReady] = useState(false);

  // Two-stage strategy to maximise perceived speed without hurting LCP:
  //
  // 1. Prefetch — import('./BackgroundCanvas') fires immediately after hydration.
  //    This starts the Three.js chunk download at background priority while the
  //    hero image (fetchPriority="high") still wins bandwidth. Webpack
  //    deduplicates the request with the dynamic() import below.
  //
  // 2. Render gate — <BackgroundCanvas /> is only mounted once window.load fires,
  //    which is the first moment we know the LCP resource has finished loading.
  //    One rAF lets the browser commit that final LCP paint before WebGL
  //    initialization begins on the main thread.
  //
  // Result: the chunk is already cached when the gate opens, so the canvas
  // appears within a single frame of window.load — much faster than
  // requestIdleCallback (which depends on CPU availability, not resource timing).
  useEffect(() => {
    // Fire-and-forget prefetch.
    import('./BackgroundCanvas').catch(() => {});

    const start = () => requestAnimationFrame(() => setCanvasReady(true));

    if (document.readyState === 'complete') {
      // Already loaded — happens on client-side navigation back to this page.
      start();
      return;
    }

    window.addEventListener('load', start, { once: true });
    return () => window.removeEventListener('load', start);
  }, []);

  // Keep the fixed canvas aligned with the visual viewport — inset: 0 tracks
  // the layout viewport and leaves a gap when the URL bar hides on mobile.
  useEffect(() => {
    const root = rootRef.current;
    const viewport = window.visualViewport;

    if (!root || !viewport) {
      return;
    }

    const sync = () => {
      root.style.height = `${viewport.height}px`;
      root.style.transform = `translateY(${viewport.offsetTop}px)`;
    };

    sync();
    // passive: true lets the compositor skip waiting for JS before scrolling.
    viewport.addEventListener('resize', sync, { passive: true });
    viewport.addEventListener('scroll', sync, { passive: true });

    return () => {
      viewport.removeEventListener('resize', sync);
      viewport.removeEventListener('scroll', sync);
    };
  }, []);

  return (
    <div ref={rootRef} className={classes.background} aria-hidden="true">
      {canvasReady && <BackgroundCanvas />}
      <div className={classes.scrim} />
    </div>
  );
};

export default TechBackground;
